import { useHasMandatoryInstructions } from "pages/project/common/taskBase/sidebar/instructions/useHasMandatoryInstructions";
import { useAcknowledgeTaskInstructionsMutation } from "query/task.query";
import { useCallback, useState } from "react";

/** Which sentence the confirmation modal shows (D16 wording differs for downloads and CAT links). */
export type InstructionAckContext = "download" | "cat";

interface PendingAction {
  action: () => void;
  context: InstructionAckContext;
}

/**
 * Gates task material downloads and CAT tool links behind a one-time confirmation when the task has
 * mandatory instructions the supplier has not acknowledged yet.
 *
 * @param enabled Prefer `false` for customers — instructions GET is supplier-only and must not run.
 */
export const useMandatoryInstructionAck = (taskId: string, enabled = true) => {
  const { mandatoryInstructions, hasMandatoryInstructions, instructionsAcknowledgedAt, isLoading, error } =
    useHasMandatoryInstructions(taskId, enabled);
  const { mutateAsync, isPending } = useAcknowledgeTaskInstructionsMutation(taskId);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  // Fail closed while the gate cannot decide: loading or a failed fetch must not skip the modal.
  const isBlocked = enabled && (isLoading || !!error);
  const needsAcknowledgement = !isBlocked && hasMandatoryInstructions && !instructionsAcknowledgedAt;

  const runOrConfirm = useCallback(
    (action: () => void, context: InstructionAckContext) => {
      if (!enabled) {
        action();
        return;
      }
      // Loading or fetch error: never open download/CAT as if there were no mandatory instructions.
      if (isBlocked) {
        return;
      }
      if (!needsAcknowledgement) {
        action();
        return;
      }
      setPendingAction({ action, context });
    },
    [enabled, isBlocked, needsAcknowledgement]
  );

  const cancel = useCallback(() => setPendingAction(null), []);

  const confirm = useCallback(async () => {
    if (!pendingAction) {
      return;
    }
    try {
      await mutateAsync();
    } catch {
      // The mutation already reports the failure; keep the modal open and do not run the action.
      return;
    }
    setPendingAction(null);
    pendingAction.action();
  }, [mutateAsync, pendingAction]);

  return {
    mandatoryInstructions,
    hasMandatoryInstructions,
    needsAcknowledgement,
    isBlocked,
    confirmationContext: pendingAction?.context ?? null,
    isConfirming: isPending,
    runOrConfirm,
    confirm,
    cancel,
  };
};
